import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { MetricBreakdown } from "@/hooks/useMetricsDetails";

interface MetricDetailTableProps {
  metricType: 'mrr' | 'arr' | 'churn' | 'revenue' | 'cac' | 'ltv' | 'average_ticket';
  breakdown: MetricBreakdown[];
  total: number;
  average?: number;
}

export function MetricDetailTable({ metricType, breakdown, total, average }: MetricDetailTableProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const renderMRRTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Produto</TableHead>
          <TableHead>Período</TableHead>
          <TableHead className="text-right">Clientes</TableHead>
          <TableHead className="text-right">Valor</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {breakdown.map((item, index) => (
          <TableRow key={index}>
            <TableCell className="font-medium">{item.product_name || 'Todos'}</TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {formatDate(item.period_start)} - {formatDate(item.period_end)}
            </TableCell>
            <TableCell className="text-right">{item.customer_count || 0}</TableCell>
            <TableCell className="text-right font-semibold">{formatCurrency(item.value)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell colSpan={3} className="font-bold">TOTAL {metricType.toUpperCase()}</TableCell>
          <TableCell className="text-right font-bold">{formatCurrency(total)}</TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );

  const renderChurnTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Produto</TableHead>
          <TableHead className="text-right">Cancelamentos</TableHead>
          <TableHead className="text-right">Total Clientes</TableHead>
          <TableHead className="text-right">Taxa de Churn</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {breakdown.map((item, index) => (
          <TableRow key={index}>
            <TableCell className="font-medium">{item.product_name || 'Todos'}</TableCell>
            <TableCell className="text-right">{item.customer_count || 0}</TableCell>
            <TableCell className="text-right">{item.total_customers || 0}</TableCell>
            <TableCell className="text-right font-semibold text-destructive">
              {formatPercent(item.churn_rate || 0)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell colSpan={3} className="font-bold">MÉDIA GERAL</TableCell>
          <TableCell className="text-right font-bold text-destructive">{formatPercent(total)}</TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );

  const renderRevenueTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Fonte</TableHead>
          <TableHead>Período</TableHead>
          <TableHead className="text-right">Transações</TableHead>
          <TableHead className="text-right">Valor</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {breakdown.map((item, index) => (
          <TableRow key={index}>
            <TableCell className="font-medium">{item.source || 'N/A'}</TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {formatDate(item.period_start)} - {formatDate(item.period_end)}
            </TableCell>
            <TableCell className="text-right">{item.transaction_count || 0}</TableCell>
            <TableCell className="text-right font-semibold">{formatCurrency(item.value)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell colSpan={3} className="font-bold">TOTAL RECEITA</TableCell>
          <TableCell className="text-right font-bold">{formatCurrency(total)}</TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );

  const renderGenericTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Produto</TableHead>
          <TableHead>Período</TableHead>
          <TableHead className="text-right">Valor</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {breakdown.map((item, index) => (
          <TableRow key={index}>
            <TableCell className="font-medium">{item.product_name || 'Todos'}</TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {formatDate(item.period_start)} - {formatDate(item.period_end)}
            </TableCell>
            <TableCell className="text-right font-semibold">{formatCurrency(item.value)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell colSpan={2} className="font-bold">
            {average ? 'MÉDIA' : 'TOTAL'}
          </TableCell>
          <TableCell className="text-right font-bold">
            {formatCurrency(average || total)}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );

  if (breakdown.length === 0) {
    return (
      <Card className="p-8 text-center text-muted-foreground">
        Nenhum dado disponível para o período selecionado
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        {metricType === 'mrr' || metricType === 'arr' ? renderMRRTable() : null}
        {metricType === 'churn' ? renderChurnTable() : null}
        {metricType === 'revenue' || metricType === 'average_ticket' ? renderRevenueTable() : null}
        {metricType === 'cac' || metricType === 'ltv' ? renderGenericTable() : null}
      </div>
    </Card>
  );
}
